// Minimal client-side parser for the agent's SSE wire format.
//
// The api-cms proxy frames events as:
//
//     event: <type>\ndata: <json-of-the-event-object>\n\n
//
// The `data:` payload is the full event object (its `type` field mirrors the `event:`
// line), so we parse the JSON and check it against the contract; the `event:` line is
// redundant. Keepalive comment lines (starting with `:`) are ignored.
import { agentEventSchema } from '@ctd/agent-contract';
import type { AgentEvent } from '@ctd/agent-contract';

/** A contract-valid event, or null for a comment, unparseable or invalid frame. */
function parseSseFrame(frame: string): AgentEvent | null {
    const dataLines: string[] = [];

    for (const line of frame.split('\n')) {
        if (line.startsWith('data:')) {
            dataLines.push(line.slice('data:'.length).trimStart());
        }
    }

    if (dataLines.length === 0) {
        return null;
    }

    try {
        const parsed = agentEventSchema.safeParse(
            JSON.parse(dataLines.join('\n'))
        );
        return parsed.success ? parsed.data : null;
    } catch {
        return null;
    }
}

/**
 * Drain all COMPLETE SSE events from a buffer, returning the parsed events plus the
 * unconsumed tail (a partial event still arriving). Call this after each chunk and keep
 * feeding the returned `rest` back in. Events are `\n\n`-delimited.
 */
export function drainSseEvents(
    buffer: string
): {
    events: AgentEvent[];
    rest: string;
} {
    const events: AgentEvent[] = [];
    let rest = buffer;

    let boundary = rest.indexOf('\n\n');
    while (boundary !== -1) {
        const frame = rest.slice(0, boundary);
        rest = rest.slice(boundary + 2);
        const event = parseSseFrame(frame);
        if (event) {
            events.push(event);
        }
        boundary = rest.indexOf('\n\n');
    }

    return { events, rest };
}
